import {
  Badge,
  BlockStack,
  Banner,
  Card,
  InlineGrid,
  InlineStack,
  Layout,
  Page,
  SkeletonBodyText,
  SkeletonPage,
  Text,
} from "@shopify/polaris";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { ApiError, shouldRetryQuery } from "../api/client";
import { getMe } from "../api/me";
import { UsageCard } from "../components/UsageCard";
import { t } from "../lib/i18n";
import { planLabel } from "../lib/plan";
import type { MeResponse } from "../types";

/**
 * 起動直後のホーム。`/api/me` を1本叩いて店・プラン・今月の使用状況を出す。
 *
 * この画面が出れば **session token 認証の経路が実機で通った**ことになる
 * ＝以降の画面（アーカイブ検索・請求書発行）は API を差し替えるだけで済む。
 */
export function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["me"],
    retry: shouldRetryQuery,
    meta: { authRecoveryReadOnly: true },
    queryFn: getMe,
  });

  if (isPending) {
    return (
      <SkeletonPage title="DenchoBox">
        <Card>
          <SkeletonBodyText lines={6} />
        </Card>
      </SkeletonPage>
    );
  }

  if (isError) {
    if (error instanceof ApiError && error.status === 423) {
      return (
        <Page title="DenchoBox">
          <Banner
            tone="info"
            title={t("旧データの削除を待っています")}
            action={{ content: t("状態を再確認"), onAction: () => void refetch() }}
          >
            <Text as="p">
              {t("削除が完了するまで、以前のデータや設定を再利用せずに待機します。完了後は再度インストール手続きを行ってください。")}
            </Text>
          </Banner>
        </Page>
      );
    }
    return (
      <Page title="DenchoBox">
        <Banner
          tone="critical"
          title={t("読み込みに失敗しました")}
          action={{ content: t("再読み込み"), onAction: () => void refetch() }}
        >
          <Text as="p">{error instanceof Error ? error.message : String(error)}</Text>
        </Banner>
      </Page>
    );
  }

  return (
    <Page
      title={t("ホーム")}
      subtitle={t("電子帳簿保存法アーカイブと適格請求書の発行状況")}
      primaryAction={{
        content: t("アーカイブを検索"),
        onAction: () => navigate({ pathname: "/archive", search: location.search }),
      }}
      secondaryActions={[
        {
          content: t("ストア表示を診断"),
          onAction: () => navigate({ pathname: "/diagnosis", search: location.search }),
        },
        {
          content: t("料金プランを見る"),
          onAction: () => navigate({ pathname: "/plans", search: location.search }),
        },
      ]}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <ShopSummary me={data} />
            <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
              <UsageCard
                title={t("今月のアーカイブ")}
                usage={data.archive_usage}
                unit="件"
                blocksOnOverLimit={false}
              />
              <UsageCard
                title={t("今月の発行")}
                usage={data.invoice_usage}
                unit="枚"
                blocksOnOverLimit
              />
            </InlineGrid>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function ShopSummary({ me }: { me: MeResponse }) {
  return (
    <Card>
      <BlockStack gap="200">
        <InlineStack gap="200" blockAlign="center">
          <Text as="span" tone="subdued">
            {t("ストア")}
          </Text>
          <Text as="span" fontWeight="semibold">
            {me.shopify_domain}
          </Text>
        </InlineStack>
        <InlineStack gap="200" blockAlign="center">
          <Text as="span" tone="subdued">
            {t("プラン")}
          </Text>
          <Badge tone={me.subscription.active ? "success" : undefined}>
            {planLabel(me.subscription.plan_code)}
          </Badge>
          <Text as="span" tone="subdued">
            {t("トライアル")}
          </Text>
          <Text as="span">{trialLabel(me.trial.remaining_days)}</Text>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

/** null＝トライアル未使用（プラン既定の日数が丸ごと残る）／0＝使い切り。 */
function trialLabel(remainingDays: number | null): string {
  if (remainingDays === null) return t("未使用");
  if (remainingDays <= 0) return t("使い切り");
  return t("残り{days}日", { days: remainingDays });
}
